class FlocageBundle extends HTMLElement {
  constructor() {
    super();
    this.quantity = 1;
    this.form = null;
    this.productForm = null;
    this.quantityBreaks = null;

    this.handleQuantityUpdate = this.onQuantityBreaksUpdated.bind(this);
    this.handleFieldUpdate = this.updateFormInputs.bind(this);
  }

  connectedCallback() {
    if (this.dataset.flocageInitialized === 'true') return;
    this.dataset.flocageInitialized = 'true';

    this.productForm = this.closest('product-form');
    this.form = this.productForm?.querySelector('form[action$="/cart/add"]') || null;
    this.quantityBreaks = this.productForm?.querySelector('[data-ref="quantity-breaks"]') || null;
    this.quantity = parseInt(this.dataset.defaultCount, 10) || 1;
    this.dataset.activeCount = String(this.quantity);

    document.addEventListener('quantity-breaks:updated', this.handleQuantityUpdate);
    this.addEventListener('input', this.handleFieldUpdate);
    this.addEventListener('change', this.handleFieldUpdate);

    window.requestAnimationFrame(() => {
      this.onQuantityBreaksUpdated();
      this.updateFormInputs();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('quantity-breaks:updated', this.handleQuantityUpdate);
    this.removeEventListener('input', this.handleFieldUpdate);
    this.removeEventListener('change', this.handleFieldUpdate);
    delete this.dataset.flocageInitialized;
  }

  onQuantityBreaksUpdated(event) {
    if (event?.detail?.quantityBreaks && event.detail.quantityBreaks !== this.quantityBreaks) return;

    const selectedBreak = this.quantityBreaks?.getSelectedQuantity?.()
      || this.productForm?.querySelector('.quantity-break--selected');

    if (!selectedBreak) return;

    const newQuantity = parseInt(selectedBreak.dataset.quantity, 10) || 1;
    this.quantity = newQuantity;
    this.updateFlocageDisplay();
    this.updateFormInputs();
  }

  updateFlocageDisplay() {
    this.dataset.activeCount = String(this.quantity);

    this.querySelectorAll('[data-group]').forEach((group) => {
      const groupNum = parseInt(group.dataset.group, 10) || 1;
      const isActive = groupNum <= this.quantity;

      group.classList.toggle('flocage-group--hidden', !isActive);
      group.hidden = !isActive;

      group.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !isActive;
      });
    });

    const count = this.querySelector('.flocage-bundle__count');
    if (count) {
      count.textContent = this.quantity === 1
        ? this.dataset.countSingular || '1 maillot'
        : (this.dataset.countPlural || '{quantity} maillots').replace('{quantity}', String(this.quantity));
    }
  }

  updateFormInputs() {
    if (!this.form) {
      this.productForm = this.closest('product-form');
      this.form = this.productForm?.querySelector('form[action$="/cart/add"]') || null;
      if (!this.form) return;
    }

    const note = this.compileFlocageNote();
    let notePreview = this.form.querySelector('[data-flocage-note-preview]');

    if (!notePreview) {
      notePreview = document.createElement('input');
      notePreview.type = 'hidden';
      notePreview.name = '_flocage_note_preview';
      notePreview.dataset.flocageNotePreview = 'true';
      this.form.appendChild(notePreview);
    }

    notePreview.value = note || '';
  }

  compileFlocageNote() {
    const parts = [];

    for (let i = 1; i <= this.quantity; i += 1) {
      const props = this.getGroupProperties(i);
      const name = props[`Flocage ${i} - Prénom`] || '';
      const number = props[`Flocage ${i} - Numéro`] || '';

      if (!name && !number) continue;

      const details = [];
      if (name) details.push(name);
      if (number) details.push(`N°${number}`);
      parts.push(`Maillot ${i} : ${details.join(' - ')}`);
    }

    return parts.join(' | ');
  }

  getGroupProperties(groupNum) {
    if (groupNum > this.quantity) return {};

    const group = this.querySelector(`[data-group="${groupNum}"]`);
    if (!group || group.hidden) return {};

    const nameInput = group.querySelector(`[name="properties[Flocage ${groupNum} - Prénom]"]`);
    const numberInput = group.querySelector(`[name="properties[Flocage ${groupNum} - Numéro]"]`);
    const properties = {};

    const name = nameInput?.value?.trim() || '';
    const number = numberInput?.value?.trim() || '';

    if (name) properties[`Flocage ${groupNum} - Prénom`] = name;
    if (number) properties[`Flocage ${groupNum} - Numéro`] = number;

    return properties;
  }

  buildBackendOrderNote(quantityBreaks = this.quantityBreaks) {
    const selectedBreak = quantityBreaks?.getSelectedQuantity?.();
    const quantity = parseInt(selectedBreak?.dataset.quantity, 10) || this.quantity || 1;
    const configurations = quantityBreaks?.getSelectedConfigurations?.() || [];
    const offerTitle = selectedBreak?.querySelector('.qb-title')?.textContent?.trim()
      || `${quantity} maillot${quantity > 1 ? 's' : ''}`;

    const noteLines = [
      this.dataset.noteTitle || 'PERSONNALISATION FLOCAGE',
      `${this.dataset.noteOfferLabel || 'Offre'} : ${offerTitle}`,
      '',
    ];

    let hasPersonalization = false;

    for (let i = 1; i <= quantity; i += 1) {
      const props = this.getGroupProperties(i);
      const name = props[`Flocage ${i} - Prénom`] || '';
      const number = props[`Flocage ${i} - Numéro`] || '';
      const variantTitle = configurations[i - 1]?.title || this.dataset.noteVariantFallback || 'Non précisée';

      if (name || number) hasPersonalization = true;

      noteLines.push(`${this.dataset.noteJerseyLabel || 'Maillot'} ${i}`);
      noteLines.push(`${this.dataset.noteVariantLabel || 'Couleur / Taille'} : ${variantTitle}`);
      noteLines.push(`${this.dataset.noteNameLabel || 'Nom'} : ${name || this.dataset.noteEmptyValue || 'Non renseigné'}`);
      noteLines.push(`${this.dataset.noteNumberLabel || 'Numéro'} : ${number || this.dataset.noteEmptyValue || 'Non renseigné'}`);
      noteLines.push('');
    }

    return hasPersonalization ? noteLines.join('\n').trim() : '';
  }
}

if (!customElements.get('flocage-bundle')) {
  customElements.define('flocage-bundle', FlocageBundle);
}
